import SectionHeader, {
    SectionHeaderButtonProps,
} from 'components/SectionHeader';
import {FC, useState} from 'react';
import {useBackend} from 'state/backend';
import {t} from 'ttag';
import {Backend} from 'types/api';

import Header from '../components/Header';
import {useSendMagicLink} from '../components/MagicLinkForm/services';
import PortalNotification, {
    MessageKind,
} from '../components/PortalNotification';
import PortalPageLayout from '../components/PortalPageLayout';
import useSessionTimer from '../hooks/useSessionTimer';
import {ClientPortal, PortalRow} from './PortalRow';

export type PortalHomeBackend = Backend<{
    email: string;
    message?: MessageKind;
    portals: ClientPortal[];
    sessionExpiration: number;
}>;

type ResendButtons = Record<ResendState, SectionHeaderButtonProps>;
type ResendState = 'error' | 'not-requested' | 'requested' | 'success';

const PortalHome: FC = () => {
    const {data} = useBackend<PortalHomeBackend>();
    const sessionStatus = useSessionTimer(data.sessionExpiration);
    const sendLink = useSendMagicLink();
    const [resendState, setResendState] =
        useState<ResendState>('not-requested');

    const onResendLink = async () => {
        setResendState('requested');
        await sendLink
            .mutateAsync({email: data.email})
            .then(() => {
                setResendState('success');
            })
            .catch(() => {
                setResendState('error');
            });
    };

    const resendButtons: ResendButtons = {
        error: {
            kind: 'destructive',
            label: t`Please try again`,
            type: 'submit',
        },
        'not-requested': {
            kind: 'primary',
            label: t`Resend link`,
            onClick: () => {
                onResendLink();
            },
            type: 'submit',
        },
        requested: {
            disabled: true,
            kind: 'primary',
            label: t`Resending...`,
            type: 'submit',
        },
        success: {
            kind: 'secondary',
            label: t`Link resent`,
            type: 'submit',
        },
    };

    const resendButton = sessionStatus.expired
        ? resendButtons[resendState] || resendButtons.error
        : undefined;

    return (
        <PortalPageLayout className="flex flex-col px-8" isResponsive={true}>
            <Header
                className="mb-6"
                email={data.email}
                sessionStatus={sessionStatus}
            />
            <PortalNotification kind={data.message} />
            <div className="mt-3">
                <SectionHeader
                    button={resendButton}
                    className="mb-6"
                    classNameSubtitle="text-base"
                    classNameTitle="mt-2 wrap-mail-text"
                    level={1}
                    subtitle={t`List of Business Portals for`}
                    subtitleTop={true}
                    title={data.email}
                />
                {data.portals.map((portal) => (
                    <PortalRow
                        disabled={sessionStatus.expired}
                        key={portal.mid}
                        portal={portal}
                    />
                ))}
            </div>
        </PortalPageLayout>
    );
};

export default PortalHome;
